import { useState } from 'react';
import { Printer, EnvelopeSimple, Link as LinkIcon, Check } from '@phosphor-icons/react';

/**
 * PrintableComparison — reusable side-by-side comparison table with a
 * Print / Email / Copy-link toolbar. Designed for both the SalesCrunchCRM
 * homepage (light variant) and the MigrationCrunch compare page (dark variant).
 *
 * Props:
 *   variant      "light" | "dark"   visual theme
 *   title        string             section heading (e.g. "How SalesCrunchCRM compares")
 *   subtitle     string             one-line context under the title
 *   shareSubject string             email subject line when "Email" is clicked
 *   columns      Array<{
 *                   id:    string   stable key
 *                   label: string   display header (e.g. "SalesCrunchCRM")
 *                   sub?:  string   small sub-label under header
 *                   isUs?: boolean  highlights this column
 *                 }>
 *   rows         Array<{
 *                   label: string                 row label (left column)
 *                   cells: Record<colId, ReactNode>  per-column value
 *                   highlight?: boolean           emphasize the row
 *                 }>
 *   footnote?    string|ReactNode   small disclaimer / pricing-as-of date
 *   testid?      string             data-testid prefix
 */
export default function PrintableComparison({
  variant = 'light',
  title,
  subtitle,
  shareSubject,
  columns,
  rows,
  footnote,
  testid = 'compare',
}) {
  const [copied, setCopied] = useState(false);
  const isDark = variant === 'dark';

  const handlePrint = () => {
    // Browser print — the global @media print CSS hides everything outside
    // [data-print-area]. See index.css for the rules.
    window.print();
  };

  const handleEmail = () => {
    const url = window.location.href;
    const body = encodeURIComponent(
      `Thought you'd find this comparison useful:\n\n${title}\n${subtitle || ''}\n\n${url}\n`
    );
    const subj = encodeURIComponent(shareSubject || title);
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // older browsers — fallback to a select-and-prompt
      window.prompt('Copy this link:', window.location.href);
    }
  };

  // Theme tokens
  const T = isDark
    ? {
        wrap: 'bg-[#0F0F1A] text-white',
        card: 'bg-white/[0.03] border border-white/10 rounded-xl',
        head: 'border-b border-white/10',
        headLabel: 'text-amber-300/90',
        rowAlt: 'odd:bg-white/[0.015]',
        rowLine: 'border-t border-white/[0.06]',
        rowLabel: 'text-white/60 text-xs uppercase tracking-wide',
        cell: 'text-white/85',
        cellMuted: 'text-white/40',
        usBg: 'bg-amber-400/10',
        usBorder: 'border-amber-400/40',
        usLabel: 'text-amber-300',
        toolbar: 'bg-white/[0.04] border border-white/10',
        btn: 'bg-white/5 hover:bg-white/10 text-white/80 hover:text-white border border-white/10',
        footnote: 'text-white/40',
      }
    : {
        wrap: 'bg-white text-slate-900',
        card: 'bg-white border border-slate-200 rounded-xl shadow-sm',
        head: 'border-b border-slate-200',
        headLabel: 'text-slate-500',
        rowAlt: 'odd:bg-slate-50/60',
        rowLine: 'border-t border-slate-100',
        rowLabel: 'text-slate-500 text-xs uppercase tracking-wide',
        cell: 'text-slate-700',
        cellMuted: 'text-slate-400',
        usBg: 'bg-amber-50',
        usBorder: 'border-amber-400',
        usLabel: 'text-amber-700',
        toolbar: 'bg-slate-50 border border-slate-200',
        btn: 'bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 border border-slate-200',
        footnote: 'text-slate-400',
      };

  return (
    <div className={`${T.wrap} px-6 py-16`} data-testid={`${testid}-section`}>
      <div className="max-w-6xl mx-auto" data-print-area>
        {/* Heading + toolbar */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight">{title}</h2>
            {subtitle && (
              <p className={`mt-2 max-w-2xl ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                {subtitle}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5 self-start md:self-end">
          <div
            className={`no-print flex items-center gap-2 ${T.toolbar} rounded-lg p-1.5`}
            data-testid={`${testid}-toolbar`}
          >
            <button
              type="button"
              onClick={handlePrint}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${T.btn}`}
              data-testid={`${testid}-print-btn`}
              title="Print this comparison"
            >
              <Printer size={14} weight="bold" /> Print
            </button>
            <button
              type="button"
              onClick={handleEmail}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${T.btn}`}
              data-testid={`${testid}-email-btn`}
              title="Email this comparison"
            >
              <EnvelopeSimple size={14} weight="bold" /> Email
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${T.btn}`}
              data-testid={`${testid}-copy-btn`}
              title="Copy link to this comparison"
            >
              {copied ? <Check size={14} weight="bold" /> : <LinkIcon size={14} weight="bold" />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
          <div
            className={`no-print text-[11px] italic text-right md:pr-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}
            data-testid={`${testid}-toolbar-hint`}
          >
            Tip — hit Print to save as PDF · Email to share with a prospect
          </div>
          </div>
        </div>

        {/* Table */}
        <div className={`${T.card} overflow-x-auto`} data-testid={`${testid}-table`}>
          <table className="w-full text-sm" style={{ minWidth: 720 }}>
            <thead>
              <tr className={T.head}>
                <th className={`text-left px-4 py-4 font-bold text-xs uppercase tracking-wider ${T.headLabel} w-48`}>
                  &nbsp;
                </th>
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className={`text-left px-4 py-4 align-bottom border-l ${isDark ? 'border-white/[0.06]' : 'border-slate-100'} ${col.isUs ? `${T.usBg}` : ''}`}
                    data-testid={`${testid}-col-${col.id}`}
                  >
                    <div className={`text-[10px] font-bold uppercase tracking-widest mb-0.5 ${col.isUs ? T.usLabel : T.headLabel}`}>
                      {col.isUs ? 'Us' : 'Them'}
                    </div>
                    <div className={`font-black text-base ${col.isUs ? T.usLabel : ''}`}>{col.label}</div>
                    {col.sub && (
                      <div className={`text-xs font-normal mt-0.5 ${isDark ? 'text-white/45' : 'text-slate-400'}`}>
                        {col.sub}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr
                  key={ri}
                  className={`${T.rowAlt} ${T.rowLine} ${row.highlight ? (isDark ? 'bg-amber-400/[0.06]' : 'bg-amber-50/70') : ''}`}
                  data-testid={`${testid}-row-${ri}`}
                >
                  <td className={`px-4 py-3 font-semibold ${T.rowLabel}`}>{row.label}</td>
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={`px-4 py-3 align-top border-l ${isDark ? 'border-white/[0.04]' : 'border-slate-100'} ${col.isUs ? T.usBg : ''} ${col.isUs ? '' : T.cell}`}
                    >
                      <span className={col.isUs ? 'font-semibold' : ''}>
                        {row.cells[col.id] ?? <span className={T.cellMuted}>—</span>}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* "Us" column emphasis border — overlay via CSS */}

        {/* Footnote */}
        {footnote && (
          <p className={`mt-4 text-xs ${T.footnote}`} data-testid={`${testid}-footnote`}>
            {footnote}
          </p>
        )}
      </div>
    </div>
  );
}
